import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [catDropdownOpen, setCatDropdownOpen] = useState(false);
  const [guideDropdownOpen, setGuideDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [mobileGuideOpen, setMobileGuideOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) setCatDropdownOpen(false);
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) setGuideDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isHome = location.pathname === "/";

  const navLinkClass = (active: boolean) =>
    `text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
      scrolled
        ? active
          ? "text-primary-500"
          : "text-foreground-700 hover:text-primary-500"
        : active
          ? "text-accent-300"
          : "text-white/90 hover:text-white"
    }`;

  const textShadowClass = scrolled ? "" : "[text-shadow:0_1px_4px_rgba(0,0,0,0.45)]";

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/95 backdrop-blur-md border-b border-background-200"
            : "bg-black/8 backdrop-blur-[2px]"
        }`}
      >
        <div className="flex items-center justify-between h-16 px-4 lg:px-6 max-w-7xl mx-auto">
          {/* Left: Brand */}
          <Link to="/" className="flex items-center gap-1.5 flex-shrink-0">
            <span
              className={`text-lg font-bold tracking-tight transition-colors duration-300 ${textShadowClass} ${
                scrolled ? "text-foreground-950" : "text-white"
              }`}
            >
              PandaBuy<span className={scrolled ? "text-primary-500" : "text-accent-300"}>.</span>Yachts
            </span>
          </Link>

          {/* Center: Desktop Nav - shows at 1024px+ */}
          <div className="hidden lg:flex items-center gap-6">
            <Link to="/" className={`${navLinkClass(isHome)} ${textShadowClass}`}>
              Home
            </Link>

            {/* Categories Dropdown */}
            <div
              ref={catRef}
              className="relative"
              onMouseEnter={() => setCatDropdownOpen(true)}
              onMouseLeave={() => setCatDropdownOpen(false)}
            >
              <button
                onClick={() => setCatDropdownOpen(!catDropdownOpen)}
                className={`flex items-center gap-1 ${navLinkClass(false)} ${textShadowClass}`}
              >
                Categories
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${catDropdownOpen ? "rotate-180" : ""}`}></i>
              </button>
              {catDropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-60 bg-white rounded-lg border border-background-200 shadow-lg py-2 max-h-80 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors"
                    >
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className={`${cat.icon} text-base`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Shopping Guide Dropdown */}
            <div
              ref={guideRef}
              className="relative"
              onMouseEnter={() => setGuideDropdownOpen(true)}
              onMouseLeave={() => setGuideDropdownOpen(false)}
            >
              <button
                onClick={() => setGuideDropdownOpen(!guideDropdownOpen)}
                className={`flex items-center gap-1 ${navLinkClass(location.pathname.includes("article") || location.pathname.includes("guide"))} ${textShadowClass}`}
              >
                Shopping Guide
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${guideDropdownOpen ? "rotate-180" : ""}`}></i>
              </button>
              {guideDropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 bg-white rounded-lg border border-background-200 shadow-lg py-2 max-h-96 overflow-y-auto z-50">
                  <Link
                    to="/pandabuy-spreadsheet-guide"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-primary-500 hover:bg-primary-50 transition-colors border-b border-background-100 mb-1"
                  >
                    <i className="ri-book-open-line text-base w-5 h-5 flex items-center justify-center"></i>
                    Complete Guide 2026
                  </Link>
                  {articles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors"
                    >
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-article-line text-base"></i>
                      </span>
                      {article.title.length > 45 ? article.title.slice(0, 45) + "..." : article.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: Desktop CTA - shows at 1024px+ */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`inline-flex items-center px-5 py-2 text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer ${
                scrolled
                  ? "bg-primary-500 text-white hover:bg-primary-600"
                  : "bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm"
              }`}
            >
              Pandabuy Sheet
              <i className="ri-arrow-right-up-line ml-1.5 text-sm"></i>
            </a>
          </div>

          {/* Tablet/Desktop Compact Nav - shows at 768px to 1023px */}
          <div className="hidden md:flex lg:hidden items-center gap-4">
            <Link to="/" className={`${navLinkClass(isHome)} ${textShadowClass}`}>
              Home
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer ${
                scrolled
                  ? "bg-primary-500 text-white hover:bg-primary-600"
                  : "bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm"
              }`}
            >
              Pandabuy Sheet
              <i className="ri-arrow-right-up-line ml-1.5 text-sm"></i>
            </a>
          </div>

          {/* Mobile/Tablet Hamburger - hidden at 1024px+ */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
              scrolled ? "text-foreground-950" : `text-white ${textShadowClass}`
            }`}
          >
            <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </nav>

      {/* Mobile Menu - hidden at 1024px+ */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)}></div>
          <div className="absolute right-0 top-0 bottom-0 w-80 bg-white shadow-2xl overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-background-200">
              <span className="text-base font-bold text-foreground-950">
                PandaBuy<span className="text-primary-500">.</span>Yachts
              </span>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100"
              >
                <i className="ri-close-line text-lg text-foreground-700"></i>
              </button>
            </div>

            <div className="p-4 flex flex-col gap-1">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center px-4 py-3.5 text-sm font-semibold text-foreground-900 bg-background-50 rounded-lg transition-colors"
              >
                <i className="ri-home-line mr-3 text-lg w-5 h-5 flex items-center justify-center text-primary-500"></i>
                Home
              </Link>

              {/* Mobile Categories - Enhanced */}
              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="flex items-center justify-between w-full px-4 py-3.5 text-sm font-semibold text-foreground-900 bg-background-50 rounded-lg transition-colors mt-1"
              >
                <span className="flex items-center">
                  <i className="ri-grid-line mr-3 text-lg w-5 h-5 flex items-center justify-center text-primary-500"></i>
                  Categories
                </span>
                <span className="text-xs text-foreground-400">{categories.length} items</span>
                <i className={`ri-arrow-down-s-line text-base ml-2 transition-transform ${mobileCatOpen ? "rotate-180" : ""}`}></i>
              </button>

              {mobileCatOpen && (
                <div className="grid grid-cols-2 gap-2 mt-2 mb-2 px-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 p-2.5 bg-background-50 hover:bg-primary-50 rounded-lg transition-colors"
                    >
                      <span className="w-7 h-7 flex items-center justify-center rounded-full bg-background-200 flex-shrink-0">
                        <i className={`${cat.icon} text-sm text-foreground-600`}></i>
                      </span>
                      <span className="text-xs font-medium text-foreground-700 leading-tight">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}

              {/* Mobile Shopping Guide */}
              <button
                onClick={() => setMobileGuideOpen(!mobileGuideOpen)}
                className="flex items-center justify-between w-full px-4 py-3.5 text-sm font-semibold text-foreground-900 bg-background-50 rounded-lg transition-colors mt-1"
              >
                <span className="flex items-center">
                  <i className="ri-book-open-line mr-3 text-lg w-5 h-5 flex items-center justify-center text-primary-500"></i>
                  Shopping Guide
                </span>
                <span className="text-xs text-foreground-400">{articles.length + 1} articles</span>
                <i className={`ri-arrow-down-s-line text-base ml-2 transition-transform ${mobileGuideOpen ? "rotate-180" : ""}`}></i>
              </button>

              {mobileGuideOpen && (
                <div className="ml-1 border-l-2 border-primary-200 pl-3 space-y-0.5 mt-1 mb-2">
                  <Link
                    to="/pandabuy-spreadsheet-guide"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                  >
                    <i className="ri-star-line text-sm w-4 h-4 flex items-center justify-center"></i>
                    Complete Guide 2026
                  </Link>
                  {articles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-600 hover:bg-background-50 rounded-lg transition-colors"
                    >
                      <i className="ri-article-line text-sm w-4 h-4 flex items-center justify-center"></i>
                      {article.title.length > 40 ? article.title.slice(0, 40) + "..." : article.title}
                    </Link>
                  ))}
                </div>
              )}

              <div className="mt-4 pt-3 border-t border-background-200">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center w-full px-5 py-3.5 text-sm font-semibold bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Pandabuy Sheet
                  <i className="ri-arrow-right-up-line ml-1.5 text-base"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}